import React from 'react'
import { Table } from 'react-bootstrap'

import OcTabLayout from '../../layout/OcTabLayout'
import useOcOrderActions from '../../hooks/useOcOrderActions'

import { OC_LINE_ERROR_MAP } from '../../constants/ocConstants'
import { safeValue, safeMoney, resolveItemTotal } from '../../helpers/tableUtils'
import { buildReprocessHandler } from '../../helpers/reprocessHelper'
import { canManagePocOrders } from '../../../../global_context/capabilities/pocCapabilities'

const OcOrderTab = ({
  lines = {},
  errors = {},
  downloads = {},
  tabsOrders = {},
  onReprocessSuccess,
  summary = {},
  last_activity_at = {},
  status = {},
  insertionErrorDetails
}) => {
  const purchase_order_lines = lines.purchase_order_lines ?? []
  const cart_items = lines.cart_items ?? []

  const canWriteReprocess = canManagePocOrders()

  const {
    downloadPurchaseOrder,
    reprocessPurchaseOrder,
    loadingDownload,
    loadingReprocess
  } = useOcOrderActions({
    purchaseOrderFileId: downloads.purchase_order_file_id,
    purchaseOrderFileName: downloads.file_name,
    orderId: tabsOrders.order_id
  })

  const cartItemsByLineId = cart_items.reduce((acc, item) => {
    if (item.purchase_order_line_id) {
      acc[item.purchase_order_line_id] = item
    }
    return acc
  }, {})

  const getLineStatus = (lineId) => {
    const errorText = errors?.[lineId]

    if (errorText) {
      const mapped = OC_LINE_ERROR_MAP[errorText]

      return {
        label: mapped?.short || 'ERROR',
        tooltip: mapped?.full || errorText,
        className: 'oc-error-style'
      }
    }

    return {
      label: 'PROCESADO',
      tooltip: 'Procesado correctamente',
      className: 'oc-process-style'
    }
  }

  const { canReprocess, handleReprocess } = buildReprocessHandler({
    tabsOrders,
    summary,
    reprocessPurchaseOrder,
    onReprocessSuccess,
    status
  })

  const actions = [
    {
      label: 'DESCARGAR OC',
      icon: 'download_2',
      className: 'download-purchase-order',
      onClick: downloadPurchaseOrder,
      disabled: !downloads.purchase_order_file_id || loadingDownload
    },

    canWriteReprocess && {
      label: 'REPROCESAR',
      icon: 'refresh',
      className: 'reload-purchase-order',
      onClick: handleReprocess,
      disabled: !canReprocess || loadingReprocess
    }
  ].filter(Boolean)

  return (
    <OcTabLayout
      summary={summary}
      tabsOrders={tabsOrders}
      last_activity_at={last_activity_at}
      title="Detalles de orden de compra"
      actions={actions}
      status={status}
      insertionErrorDetails={insertionErrorDetails}
    >
      <div className="d-flex justify-content-between gap-4">
        <div style={{ width: '55%' }}>
          <b>Productos en pedido:</b>

          <Table hover responsive className="mt-2 oc-fixed-table">
            <thead className="table-header-gray-poc">
              <tr>
                <th className="col-w-120">CÓDIGO BARRA</th>
                <th className="col-w-260">DESCRIPCIÓN</th>
                <th className="col-w-70">CANT.</th>
                <th className="col-w-100">EMPAQUE</th>
                <th className="col-w-100">PRECIO</th>
                <th className="col-w-110">TOTAL</th>
                <th className="col-w-140">ESTADO</th>
              </tr>
            </thead>
            <tbody>
              {purchase_order_lines.length === 0 ? (
                <tr>
                  <td colSpan="7">No hay resultados</td>
                </tr>
              ) : (
                purchase_order_lines.map(line => {
                  const status = getLineStatus(line.id)

                  return (
                    <tr key={line.id}>
                      <td className="oc-td-text">{safeValue(line.barcode)}</td>
                      <td className="oc-td-text">{safeValue(line.product_name)}</td>
                      <td className="oc-td-text">{safeValue(line.quantity)}</td>
                      <td className="oc-td-text">{safeValue(line.unit_of_measure)}</td>
                      <td className="oc-td-money">{safeMoney(line.unit_price)}</td>
                      <td className="oc-td-money">{safeMoney(line.total_price)}</td>
                      <td className="oc-td-status">
                        <span
                          title={status.tooltip}
                          className={`oc-order-status ${status.className}`}
                        >
                          {status.label}
                        </span>
                      </td>
                    </tr>
                  )
                })
              )}
            </tbody>
          </Table>
        </div>
        <div style={{ width: '45%' }}>
          <b>Productos procesados:</b>

          <Table hover responsive className="mt-2 oc-fixed-table">
            <thead className="table-header-gray-poc">
              <tr>
                <th className="col-w-140">CÓDIGO BARRA</th>
                <th className="col-w-260">DESCRIPCIÓN</th>
                <th className="col-w-80">CANT.</th>
                <th className="col-w-120">EMPAQUE</th>
                <th className="col-w-110">TOTAL</th>
              </tr>
            </thead>
            <tbody>
              {purchase_order_lines.length === 0 ? (
                <tr>
                  <td colSpan="5">No hay resultados</td>
                </tr>
              ) : (
                purchase_order_lines.map(line => {
                  const cartItem = cartItemsByLineId[line.id]

                  return (
                    <tr key={line.id}>
                      <td className="oc-td-text">
                        {safeValue(cartItem?.barcode || cartItem?.package_db_ref)}
                      </td>
                      <td className="oc-td-text">
                        {safeValue(cartItem?.product_name)}
                      </td>
                      <td className="oc-td-money">
                        {safeValue(cartItem?.quantity)}
                      </td>
                      <td className="oc-td-text">
                        {safeValue(cartItem?.package_unit)}
                      </td>
                      <td className="oc-td-money">
                        {safeMoney(cartItem?.total ?? resolveItemTotal(cartItem))}
                      </td>
                    </tr>
                  )
                })
              )}
            </tbody>
          </Table>
        </div>
      </div>
    </OcTabLayout>
  )
}

export default OcOrderTab
